/**
 * @owner @Appboy/ai-decisioning-experience
 */
import { useCallback, useMemo } from "react";

import type { I18nT } from "@lib/dashboardI18n";

import { isDecisioningCreativeComponentName } from "../isDecisioningCreativeComponentName";
import type { DecisioningCreativeComponentName } from "../types";

/**
 * @param t - The `preview-and-test` i18n translation function
 * @return A localizer function that maps a decisioning creative component name to its
 * user-facing label, falling back to the raw name for unrecognized components
 */
export const useDecisioningCreativeComponentNameLocalizer = (t: I18nT<"preview-and-test">) => {
  const labelsByComponentName: Record<DecisioningCreativeComponentName, string> = useMemo(
    () => ({
      cta: t("decisioning-creative-component-cta", "Call to action"),
      subject_line: t("decisioning-creative-component-subject-line", "Subject line"),
      image: t("decisioning-creative-component-image", "Image"),
    }),
    [t]
  );

  return useCallback(
    (componentName: string): string => {
      if (isDecisioningCreativeComponentName(componentName)) {
        return labelsByComponentName[componentName];
      }

      return componentName;
    },
    [labelsByComponentName]
  );
};
